import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "../styles/Login.css";
import { connectSocket } from "../socket";
import registerDeviceToken from "../utils/registerDeviceToken"; // helper to register token

export default function Login() {
  const { login } = useContext(AuthContext);
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);
  const navigate = useNavigate();

  // ✅ Called by native WebView or browser after getting token
  useEffect(() => {
    window.setFcmToken = (token) => {
      console.log("📲 Got FCM token from client:", token);
      setFcmToken(token);
      localStorage.setItem("fcmToken", token);

      // If user already logged in, register immediately
      if (localStorage.getItem("token")) {
        registerDeviceToken(token); // send to backend
      }
    };

    // Restore token on reload
    const saved = localStorage.getItem("fcmToken");
    if (saved) setFcmToken(saved);
  }, []);

  // ✅ Auto-redirect if already logged in
  useEffect(() => {
    const existingUser = localStorage.getItem("user");
    const existingToken = localStorage.getItem("token");
    if (existingUser && existingToken) {
      try {
        const user = JSON.parse(existingUser);
        if (user?.role === "admin") navigate("/admin");
        else if (user?.role) navigate(`/dashboard/${user.role}`);
      } catch {
        console.warn("⚠️ Failed to parse stored user");
      }
    }
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(form);

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?._id) throw new Error("User ID missing, cannot join socket room");

      // ✅ Connect socket after login
      const socket = connectSocket(user._id);
      socket.on("newNotification", ({ type, message }) => {
        toast.info(`${type}: ${message}`);
      });

      toast.success("Login successful! 🎉");

      // ✅ Register token if already injected
      if (fcmToken) {
        await registerDeviceToken(fcmToken);
      }

      // ✅ Redirect based on role
      if (user?.role === "admin") navigate("/admin");
      else if (user?.role) navigate(`/dashboard/${user.role}`);
      else throw new Error("User role missing.");
    } catch (err) {
      const message =
        typeof err.response?.data?.msg === "string"
          ? err.response.data.msg
          : err.message || "Login failed.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <p className="login-subtitle">Log in to access your dashboard</p>
        <form onSubmit={handleSubmit} className="login-form">
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
          <div className="password-input-wrapper">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <span
              className="toggle-password"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </div>

          {/* ✅ Forgot Password Link */}
          <div className="forgot-password">
            <Link to="/forgot-password">Forgot your password?</Link>
          </div>

          {error && <p className="error-msg">{error}</p>}
          <button type="submit" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
        <p className="signup-redirect">
          Don't have an account? <Link to="/">Sign up here</Link>
        </p>
      </div>
    </div>
  );
}
